import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';

// Build With Christ Brand Colors
const BWC_COLORS = {
  primary: '#0284c7',      // Sky Blue (sky-600)
  primaryLight: '#0ea5e9', // Sky-500
  secondary: '#059669',    // Emerald Green
  dark: '#0f172a',         // Navy Background
  lightBg: '#f0f9ff',      // Sky-50
  cardBg: '#f8fafc',       // Slate-50
  text: '#0f172a',
  textMuted: '#475569',    // Slate-600
  border: '#e2e8f0',       // Slate-200
};

// Styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: BWC_COLORS.primary,
  },
  brandName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
  },
  heroImage: {
    width: '100%',
    height: 100,
    objectFit: 'cover',
    marginBottom: 16,
    borderRadius: 4,
  },
  badge: {
    backgroundColor: BWC_COLORS.primary,
    color: '#ffffff',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 8,
    fontWeight: 'bold',
    alignSelf: 'flex-start',
    marginBottom: 6,
  },
  eventTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 4,
  },
  causeText: {
    fontSize: 13,
    color: BWC_COLORS.textMuted,
    marginBottom: 12,
  },
  causeHighlight: {
    color: BWC_COLORS.primary,
    fontWeight: 'bold',
  },
  logisticsRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
    backgroundColor: BWC_COLORS.lightBg,
    padding: 12,
    borderRadius: 6,
  },
  logisticsItem: {
    flex: 1,
  },
  logisticsLabel: {
    fontSize: 8,
    color: BWC_COLORS.textMuted,
    textTransform: 'uppercase',
    marginBottom: 3,
    letterSpacing: 0.5,
  },
  logisticsValue: {
    fontSize: 10,
    color: BWC_COLORS.dark,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 8,
    color: BWC_COLORS.primary,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 6,
    fontWeight: 'bold',
  },
  sectionHeading: {
    fontSize: 16,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 10,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    color: BWC_COLORS.textMuted,
    marginBottom: 10,
  },
  bulletList: {
    marginBottom: 12,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  bullet: {
    width: 14,
    fontSize: 10,
    color: BWC_COLORS.primary,
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: BWC_COLORS.textMuted,
    lineHeight: 1.4,
  },
  experienceGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 18,
  },
  experienceCard: {
    flex: 1,
    backgroundColor: BWC_COLORS.cardBg,
    padding: 10,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: BWC_COLORS.primary,
  },
  experienceTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 3,
  },
  experienceDescription: {
    fontSize: 8,
    color: BWC_COLORS.textMuted,
    lineHeight: 1.4,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: BWC_COLORS.border,
    marginVertical: 16,
  },
  trackCard: {
    backgroundColor: BWC_COLORS.cardBg,
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: BWC_COLORS.primary,
  },
  trackTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 3,
  },
  trackDescription: {
    fontSize: 9,
    color: BWC_COLORS.textMuted,
    lineHeight: 1.4,
  },
  qrSection: {
    marginTop: 'auto',
    paddingTop: 16,
    borderTopWidth: 2,
    borderTopColor: BWC_COLORS.primary,
    alignItems: 'center',
  },
  qrTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 10,
  },
  qrImage: {
    width: 100,
    height: 100,
    marginBottom: 8,
  },
  qrLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: BWC_COLORS.dark,
    marginBottom: 3,
  },
  qrUrl: {
    fontSize: 9,
    color: BWC_COLORS.primary,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 32,
    right: 32,
    textAlign: 'center',
    fontSize: 8,
    color: BWC_COLORS.textMuted,
  },
  calloutBox: {
    backgroundColor: BWC_COLORS.lightBg,
    borderWidth: 1,
    borderColor: BWC_COLORS.primary,
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
  },
  calloutText: {
    fontSize: 11,
    fontStyle: 'italic',
    color: BWC_COLORS.dark,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 24,
    right: 32,
    fontSize: 8,
    color: BWC_COLORS.textMuted,
  },
});

interface Event {
  id: string;
  name: string;
  slug: string;
  event_date: string;
  event_time: string | null;
  doors_time: string | null;
  location: string;
  description: string | null;
  cause: string;
  is_featured: boolean;
  is_tentative: boolean;
}

interface Challenge {
  id: string;
  one_liner: string | null;
  problem_description: string | null;
  who_it_affects: string | null;
  why_it_matters: string | null;
  opportunity: string | null;
  solution_directions: string[] | null;
  success_metrics: string | null;
}

interface EventPDFDocumentProps {
  event: Event;
  challenge: Challenge | null;
  venueImageUrl?: string;
}

const formatEventDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return format(date, 'EEEE, MMMM d, yyyy');
};

const formatTime = (timeStr: string | null) => {
  if (!timeStr) return null;
  if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

const EventPDFDocument: React.FC<EventPDFDocumentProps> = ({ event, challenge }) => {
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://buildwithchrist.ai')}&format=png`;

  // Parse bullet points from text
  const parseBulletPoints = (text: string | null): string[] => {
    if (!text) return [];
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => line.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  };

  const experiencePillars = [
    { title: 'Entertain', description: 'Enjoy live worship, fun challenges, and creative competitions with AI.' },
    { title: 'Educate', description: 'Learn cutting-edge AI skills through hands-on workshops and mentorship.' },
    { title: 'Elevate', description: 'Build real solutions that serve and uplift your community.' },
  ];

  const tracks = [
    { title: 'Virtual Participant', description: 'Join remotely via livestream to watch, learn, and participate from anywhere.' },
    { title: 'In-Person Attendee', description: 'Experience the full event on-site with networking, workshops, and collaboration.' },
    { title: 'Build Team Member', description: 'Work hands-on with a team to create AI solutions for the cause.' },
  ];

  return (
    <Document>
      {/* Page 1: Overview */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.brandName}>BuildWithChrist.ai</Text>
          <Text style={{ fontSize: 9, color: BWC_COLORS.textMuted }}>Event Information</Text>
        </View>

        {/* Event Badge & Title */}
        {event.is_featured && (
          <Text style={styles.badge}>FEATURED EVENT</Text>
        )}
        <Text style={styles.eventTitle}>{event.name}</Text>
        <Text style={styles.causeText}>
          Building for: <Text style={styles.causeHighlight}>{event.cause}</Text>
        </Text>

        {/* Logistics */}
        <View style={styles.logisticsRow}>
          <View style={styles.logisticsItem}>
            <Text style={styles.logisticsLabel}>Date</Text>
            <Text style={styles.logisticsValue}>{formatEventDate(event.event_date)}</Text>
          </View>
          {(event.doors_time || event.event_time) && (
            <View style={styles.logisticsItem}>
              <Text style={styles.logisticsLabel}>Time</Text>
              {event.doors_time && (
                <Text style={styles.logisticsValue}>Doors: {formatTime(event.doors_time)}</Text>
              )}
              {event.event_time && (
                <Text style={styles.logisticsValue}>Start: {formatTime(event.event_time)}</Text>
              )}
            </View>
          )}
          <View style={styles.logisticsItem}>
            <Text style={styles.logisticsLabel}>Location</Text>
            <Text style={styles.logisticsValue}>{event.location}</Text>
          </View>
        </View>

        {/* The Experience */}
        <Text style={styles.sectionTitle}>The Experience</Text>
        <View style={styles.experienceGrid}>
          {experiencePillars.map((pillar, index) => (
            <View key={index} style={styles.experienceCard}>
              <Text style={styles.experienceTitle}>{pillar.title}</Text>
              <Text style={styles.experienceDescription}>{pillar.description}</Text>
            </View>
          ))}
        </View>

        {/* Challenge Section */}
        {challenge && (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>The Challenge</Text>
            <Text style={styles.sectionHeading}>Building for {event.cause}</Text>

            {challenge.one_liner && (
              <View style={styles.calloutBox}>
                <Text style={styles.calloutText}>"{challenge.one_liner}"</Text>
              </View>
            )}

            {challenge.why_it_matters && (
              <Text style={styles.paragraph}>
                {truncateText(challenge.why_it_matters, 400)}
              </Text>
            )}

            {challenge.solution_directions && challenge.solution_directions.length > 0 && (
              <>
                <Text style={{ ...styles.sectionTitle, marginTop: 12 }}>What Could Be Built</Text>
                <View style={styles.bulletList}>
                  {challenge.solution_directions.slice(0, 4).map((direction, index) => (
                    <View key={index} style={styles.bulletItem}>
                      <Text style={styles.bullet}>•</Text>
                      <Text style={styles.bulletText}>{truncateText(direction, 100)}</Text>
                    </View>
                  ))}
                </View>
              </>
            )}
          </>
        )}

        {/* QR Code */}
        <View style={styles.qrSection}>
          <Text style={styles.qrTitle}>Ready to Make an Impact?</Text>
          <Image src={qrCodeUrl} style={styles.qrImage} />
          <Text style={styles.qrLabel}>Scan to Register</Text>
          <Text style={styles.qrUrl}>buildwithchrist.ai</Text>
        </View>
      </Page>

      {/* Page 2: Participation Tracks */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.brandName}>BuildWithChrist.ai</Text>
          <Text style={{ fontSize: 9, color: BWC_COLORS.textMuted }}>{event.name}</Text>
        </View>

        <Text style={styles.sectionTitle}>Ways to Participate</Text>
        <Text style={styles.sectionHeading}>Choose Your Experience</Text>
        <Text style={styles.paragraph}>
          Whether you're joining remotely or in person, there's a place for you at this event. 
          All skill levels are welcome – from complete beginners to experienced developers.
        </Text>

        {/* Track Cards */}
        {tracks.map((track, index) => (
          <View key={index} style={styles.trackCard}>
            <Text style={styles.trackTitle}>{track.title}</Text>
            <Text style={styles.trackDescription}>{track.description}</Text>
          </View>
        ))}

        <View style={styles.divider} />

        {/* What to Expect */}
        <Text style={styles.sectionTitle}>What to Expect</Text>
        <View style={styles.bulletList}>
          <View style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>Hands-on AI workshops suitable for all skill levels</Text>
          </View>
          <View style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>Mentorship from experienced developers and AI practitioners</Text>
          </View>
          <View style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>Networking opportunities with like-minded builders</Text>
          </View>
          <View style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>Real-world impact through solutions for community causes</Text>
          </View>
          <View style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>Family-friendly environment with childcare available</Text>
          </View>
        </View>

        <Text style={styles.pageNumber}>Page 2</Text>
      </Page>
    </Document>
  );
};

export default EventPDFDocument;
